import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, useCartSummary, useCartLoading, useCartError, useCartToast } from '../contexts';
import { CartItem } from '../components/molecules/CartItem';
import Button from '../components/atoms/Button';
import LogoSpinner from '../components/atoms/LogoSpinner';
import VatNotification from '../components/atoms/VatNotification';
import CouponField from '../components/atoms/CouponField';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import PromotionalBanner from '../components/molecules/PromotionalBanner';
import { CartItem as CartItemType, NavLink } from '../types';
import './CartPage.css';
import { useStorefrontData } from '../hooks/useStorefrontData';

interface CartPageProps {
  className?: string;
}

export const CartPage: React.FC<CartPageProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { cart, operations } = useCart();
  const { itemCount, totalItems, subtotal, vat, vatPercentage, isInclusive, shipping, couponDiscount, total, isEmpty } = useCartSummary();
  
  // Debug: Log cart summary values
  useEffect(() => {
    console.log('🛒 CartPage - Cart Summary:', {
      itemCount,
      totalItems,
      subtotal,
      vat,
      vatPercentage,
      isInclusive,
      shipping,
      total,
      isEmpty
    });
  }, [itemCount, totalItems, subtotal, vat, vatPercentage, isInclusive, shipping, total, isEmpty]);
  const loading = useCartLoading();
  const error = useCartError();
  const { showRemoveFromCartSuccess, showQuantityUpdated, showCartCleared, showFreeShippingProgress } = useCartToast();
  
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);
  const [showVatBreakdown, setShowVatBreakdown] = useState(true);
  const [showVatNotification, setShowVatNotification] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  // const [lastShownShippingNotification, setLastShownShippingNotification] = useState<number | null>(null);
  const { getPromotionalBannerData } = useStorefrontData();
  const bannerData = getPromotionalBannerData();

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  // Handle page title and meta
  useEffect(() => {
    document.title = `Shopping Cart (${totalItems} items) - The Frosting Room`;
  }, [totalItems]);

  // VAT calculations are now handled automatically by CartOperations
  // No need for manual VAT updates here

  // Free shipping progress notification - COMMENTED OUT
  // Only show if shipping is NOT already free and subtotal is below threshold
  // useEffect(() => {
  //   if (!isEmpty && subtotal > 0 && shipping > 0 && subtotal < 50) {
  //     const remainingAmount = 50 - subtotal;
  //     // Only show notification if user is close to free shipping (within €10)
  //     // Prevent duplicate notifications by tracking the last shown amount
  //     if (remainingAmount <= 10 && remainingAmount > 0 && lastShownShippingNotification !== remainingAmount) {
  //       showFreeShippingProgress(remainingAmount);
  //       setLastShownShippingNotification(remainingAmount);
  //     }
  //   } else if (shipping === 0 || subtotal >= 50) {
  //     // Reset notification tracking when shipping becomes free or threshold is reached
  //     setLastShownShippingNotification(null);
  //   }
  // }, [subtotal, isEmpty, shipping, showFreeShippingProgress, lastShownShippingNotification]);

  const handleContinueShopping = () => {
    navigate('/');
  };

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
  };

  const handleBannerClick = (): void => {
    console.log('Banner arrow clicked');
  };

  const handleCheckout = async () => {
    if (isEmpty) return;
    
    setIsCheckingOut(true);
    
    try {
      // Simulate checkout process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to checkout page (or implement checkout logic)
      navigate('/checkout');
    } catch (error) {
      console.error('Checkout failed:', error);
      // Handle checkout error
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    const item = cart.items.find(item => item.id === itemId);
    await operations.removeItem(itemId);
    setShowRemoveConfirm(null);
    
    if (item) {
      showRemoveFromCartSuccess(item.name);
    }
  };

  const handleUpdateQuantity = async (itemId: string, quantity: number) => {
    if (quantity < 1) {
      setShowRemoveConfirm(itemId);
      return;
    }
    
    const item = cart.items.find(item => item.id === itemId);
    await operations.updateQuantity(itemId, quantity);
    
    if (item) {
      showQuantityUpdated(item.name, quantity);
    }
  };

  const handleClearCart = async () => {
    if (window.confirm('Are you sure you want to clear your cart? This action cannot be undone.')) {
      await operations.clearCart();
      showCartCleared();
    }
  };

  const handleApplyCoupon = async (code: string) => {
    setIsApplyingCoupon(true);
    setCouponError(null);
    
    try {
      const success = await operations.applyCoupon(code);
      if (success) {
        setCouponCode('');
        // You can add a success toast here if needed
      } else {
        // Handle the case where applyCoupon returns false (coupon not found/invalid)
        setCouponError('Coupon not found');
      }
    } catch (error) {
      setCouponError(error instanceof Error ? error.message : 'Failed to apply coupon');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    await operations.removeCoupon();
    setCouponError(null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const renderEmptyState = () => (
    <div className="cart-page__empty">
      <div className="cart-page__empty-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 16.1 19 15 19H9C7.9 19 7 18.1 7 17V13M17 13H7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="cart-page__empty-title">Your cart is empty</h1>
      <p className="cart-page__empty-message">
        Looks like you haven't added any delicious treats to your cart yet. 
        Browse our collection and find something sweet!
      </p>
      <div className="cart-page__empty-actions">
        <Button
          onClick={handleContinueShopping}
          variant="primary"
          size="large"
          className="cart-page__empty-button"
        >
          Homepage
        </Button>
        <Button
          onClick={() => navigate('/catalog')}
          variant="secondary"
          size="large"
          className="cart-page__empty-button"
        >
          View Collections
        </Button>
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className="cart-page__loading">
      <LogoSpinner size="large" text="Loading your cart..." />
    </div>
  );

  const renderErrorState = () => (
    <div className="cart-page__error">
      <div className="cart-page__error-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2>Something went wrong</h2>
      <p>{error}</p>
      <div className="cart-page__error-actions">
        <Button
          onClick={() => window.location.reload()}
          variant="primary"
          size="medium"
        >
          Try Again
        </Button>
        <Button
          onClick={handleClearCart}
          variant="secondary"
          size="medium"
        >
          Clear Cart
        </Button>
      </div>
    </div>
  );

  const renderCartItems = () => (
    <div className="cart-page__items">
      <div className="cart-page__items-header">
        <h2 className="cart-page__items-title">
          Shopping Cart ({totalItems} {totalItems === 1 ? 'item' : 'items'})
        </h2>
        {!isEmpty && (
          <Button
            onClick={handleClearCart}
            variant="secondary"
            size="small"
            className="cart-page__clear-button"
          >
            Clear Cart
          </Button>
        )}
      </div>
      
      <div className="cart-page__items-list">
        {cart.items.map((item: CartItemType) => (
          <div key={item.id} className="cart-page__item-wrapper">
            <CartItem
              item={item}
              variant="detailed"
              showRemoveButton={true}
              showQuantityControls={true}
              onRemove={() => setShowRemoveConfirm(item.id)}
              onQuantityChange={handleUpdateQuantity}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderOrderSummary = () => (
    <div className="cart-page__summary">
      <div className="cart-page__summary-header">
        <h3 className="cart-page__summary-title">Order Summary</h3>
        {vat > 0 && (
          <div className="cart-page__vat-toggle">
            <label className="cart-page__vat-toggle-label">
              <input
                type="checkbox"
                checked={showVatBreakdown}
                onChange={(e) => setShowVatBreakdown(e.target.checked)}
                className="cart-page__vat-toggle-input"
              />
              <span className="cart-page__vat-toggle-text">Show VAT breakdown</span>
            </label>
          </div>
        )}
      </div>
      
      <div className="cart-page__summary-details">
        <div className="cart-page__summary-line">
          <span>Subtotal ({totalItems} items)</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        
        {vat > 0 && showVatBreakdown && (
          <div className="cart-page__summary-line">
            <span>
              {isInclusive ? `VAT (${vatPercentage}%) - Included` : `VAT (${vatPercentage}%)`}
            </span>
            <span>{formatPrice(vat)}</span>
          </div>
        )}
        
        <div className="cart-page__summary-line">
          <span>Shipping</span>
          <span>
            {shipping === 0 ? (
              <span className="cart-page__free-shipping">FREE</span>
            ) : (
              formatPrice(shipping)
            )}
          </span>
        </div>
        
        {couponDiscount > 0 && (
          <div className="cart-page__summary-line cart-page__summary-line--discount">
            <span>Coupon Discount ({cart.coupon?.code})</span>
            <span className="cart-page__discount-amount">
              -{formatPrice(couponDiscount)}
            </span>
          </div>
        )}
        
        <div className="cart-page__summary-line cart-page__summary-line--total">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>

      <div className="cart-page__coupon-section">
        <CouponField
          value={couponCode}
          onChange={setCouponCode}
          onApply={handleApplyCoupon}
          onRemove={handleRemoveCoupon}
          loading={isApplyingCoupon}
          appliedCoupon={cart.coupon}
          className="cart-page__coupon-field"
        />
        
        {couponError && (
          <div className="cart-page__coupon-error">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{couponError}</span>
          </div>
        )}
      </div>

      {/* {subtotal < 50 && (
        <div className="cart-page__shipping-notice">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            Add {formatPrice(50 - subtotal)} more for free shipping!
          </span>
        </div>
      )} */}

      <div className="cart-page__checkout-actions">
        <Button
          onClick={handleCheckout}
          variant="primary"
          size="large"
          className="cart-page__checkout-button"
          disabled={isEmpty || isCheckingOut}
        >
          {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
        </Button>
        
        <Button
          onClick={handleContinueShopping}
          variant="secondary"
          size="large"
          className="cart-page__continue-button"
        >
          Continue Shopping
        </Button>
      </div>
    </div>
  );

  const renderRemoveConfirm = () => {
    if (!showRemoveConfirm) return null;
    
    const item = cart.items.find(item => item.id === showRemoveConfirm);
    if (!item) return null;

    return (
      <div className="cart-page__modal-overlay">
        <div className="cart-page__modal">
          <h3>Remove Item</h3>
          <p>Are you sure you want to remove "{item.name}" from your cart?</p>
          <div className="cart-page__modal-actions">
            <Button
              onClick={() => setShowRemoveConfirm(null)}
              variant="secondary"
              size="medium"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleRemoveItem(item.id)}
              variant="primary"
              size="medium"
            >
              Remove
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
        <div className={`cart-page ${className}`}>
          <div className="cart-page__container">
            {renderLoadingState()}
          </div>
        </div>
        <Footer 
          onEmailSubmit={handleEmailSubmit}
          onFollowShop={handleFollowShop}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
        <div className={`cart-page ${className}`}>
          <div className="cart-page__container">
            {renderErrorState()}
          </div>
        </div>
        <Footer 
          onEmailSubmit={handleEmailSubmit}
          onFollowShop={handleFollowShop}
        />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
        <div className={`cart-page ${className}`}>
          <div className="cart-page__container">
            {renderEmptyState()}
          </div>
        </div>
        <Footer 
          onEmailSubmit={handleEmailSubmit}
          onFollowShop={handleFollowShop}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
      <div className={`cart-page ${className}`}>
        <div className="cart-page__container">
          <div className="cart-page__content">
            <div className="cart-page__main">
              {vat > 0 && showVatNotification && (
                <VatNotification
                  vatPercentage={vatPercentage}
                  vatAmount={vat}
                  isInclusive={isInclusive}
                  onDismiss={() => setShowVatNotification(false)}
                />
              )}
              {renderCartItems()}
            </div>
            
            <div className="cart-page__sidebar">
              {renderOrderSummary()}
            </div>
          </div>
        </div>
        
        {renderRemoveConfirm()}
      </div>
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default CartPage;
